import { useQuery } from "@tanstack/react-query";
import { PhoneOutline24Icon } from "@vygruppen/spor-icon-react";
import { CountryCode } from "@vygruppen/vy-train-map";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import {
  SharedGridPadding,
  SharedGridStyleNoPadding,
} from "features/CenterContent/shared/MainContentGrid";
import { handleCopyToClipboard } from "features/CenterContent/shared/utils";
import { HeadedContainerSticky } from "features/CenterContent/VehicleDetails/shared/HeadedContainerSticky";
import { DayPlan } from "features/CenterContent/VehicleDetails/TrainDetails/TrainStaff/DayPlan/DayPlan";
import { trainStaffSchema } from "features/CenterContent/VehicleDetails/TrainDetails/TrainStaff/types";
import {
  getPhoneNumberWithNOPrefix,
  getPhoneNumberWithoutNOPrefix,
} from "features/Header/PuzzelTelephone/utils";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { CallButton } from "shared/components/buttons/CallButton";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import {
  Box,
  Button,
  DropsExpandable,
  Flex,
  HStack,
  Text,
} from "shared/components/Spor";
import { TextTooltip } from "shared/components/TextTooltip";
import { useTheme } from "styled-components";
import * as z from "zod";

type StaffProps = {
  nominalDate: string | undefined;
  trainId: string | undefined;
  countryCode: CountryCode | undefined;
  isNorwegianTrainInSweden?: boolean;
};

const TrainStaff: FC<StaffProps> = ({
  nominalDate,
  trainId,
  countryCode,
  isNorwegianTrainInSweden,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { status, data } = useQuery({
    queryKey: ["staffOnBoard", countryCode, nominalDate, trainId],
    queryFn: async ({ signal }) =>
      axiosClient
        .get<z.infer<typeof trainStaffSchema>>(
          `${getBackendUrl()}/crew/${trainId}/${nominalDate}/${countryCode}`,
          {
            signal,
          },
        )
        .then((res) => trainStaffSchema.parse(res.data)),
  });

  const getContent = () => {
    switch (status) {
      case "pending":
        return <SkeletonLoader skeletonType="list" />;
      case "success": {
        const { staff } = data;

        if (staff.length > 0) {
          return (
            <Flex flexDirection="column" gap="12px">
              {staff.map((employee) => (
                <DropsExpandable
                  key={employee.employeeNumber + employee.role}
                  borderRadius="0px"
                  title={
                    <Flex w="100%" my="12px">
                      <HStack
                        flexWrap="wrap"
                        justifyContent="space-between"
                        width="100%"
                      >
                        <Text variant="sm" fontWeight={600}>
                          {employee.name ?? t("Ukjent navn")} (
                          {employee.employeeNumber})
                        </Text>

                        {employee.gsmrPhoneNumber &&
                          (isNorwegianTrainInSweden ? (
                            <TextTooltip
                              showArrow
                              placement="top"
                              label={t("Kopiér telefonnummer")}
                            >
                              <Button
                                leftIcon={<PhoneOutline24Icon />}
                                variant="ghost"
                                size="xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleCopyToClipboard(
                                    getPhoneNumberWithNOPrefix(
                                      employee.gsmrPhoneNumber!,
                                    ),
                                  );
                                }}
                              >
                                <Text variant="sm">
                                  {`${getPhoneNumberWithNOPrefix(employee.gsmrPhoneNumber)} (${employee.role})`}
                                </Text>
                              </Button>
                            </TextTooltip>
                          ) : (
                            <CallButton
                              phoneNumber={getPhoneNumberWithoutNOPrefix(
                                employee.gsmrPhoneNumber,
                              )}
                              size="xs"
                              label={employee.role}
                              insideButton
                            />
                          ))}
                      </HStack>
                    </Flex>
                  }
                  backgroundColor={theme.colorBackgroundMain}
                  collapsible
                >
                  <Box m="-12px -6px">
                    <DayPlan staff={employee} trainId={trainId} />
                  </Box>
                </DropsExpandable>
              ))}
            </Flex>
          );
        }
        return (
          <NoDataMessage
            reason={t("Fant ingen informasjon om bemanning på dette toget.")}
          />
        );
      }
      case "error":
        return <FailureMessage />;
    }
  };

  return (
    <HeadedContainerSticky
      panelStyle={SharedGridStyleNoPadding}
      variant="main"
      headerHeight="54px"
      header={
        <Text fontWeight={600} variant="sm" mr="auto" p={SharedGridPadding}>
          {t("Bemanning")}
        </Text>
      }
    >
      {getContent()}
    </HeadedContainerSticky>
  );
};

export default withErrorBoundary(TrainStaff);
